import { toast } from "react-toastify";

import {
  VENDOR_REGISTER_FAIL,
  VENDOR_REGISTER_REQUEST,
  VENDOR_REGISTER_SUCCESS,
  GET_SELLERS,
  GET_SELLERS_FAIL,
  GET_SELLER,
  GET_SELLER_FAIL,
  DELETE_SELLER,
  UPDATE_SELLER_REQUEST,
  UPDATE_SELLER_SUCCESS,
  UPDATE_SELLER_FAIL,
  FETCH_ALL_VENDORS_FOR_SUPERADMIN_REQUEST,
  FETCH_ALL_VENDORS_FOR_SUPERADMIN_SUCCESS,
  FETCH_ALL_VENDORS_FOR_SUPERADMIN_FAILURE,
} from "../constants/actionTypes.js";
import * as api from "../api/index.js";
import { START_LOADING } from "constants/webOrderConstants.js";
import { END_LOADING } from "constants/webOrderConstants.js";

// Vendor Registration Action
export const submitVendorRegistration =
  (vendorFormData) => async (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        dispatch({ type: VENDOR_REGISTER_REQUEST });

        const { data } = await api.submitVendorRegistration(vendorFormData);
        dispatch({ type: VENDOR_REGISTER_SUCCESS, payload: data });
        toast.success(data.message);
        resolve(data);
      } catch (error) {
        console.log(error);
        const errorMessage = 
          error.response?.data?.message || 
          error.message || 
          "Registration failed. Please try again.";
        
        dispatch({
          type: VENDOR_REGISTER_FAIL,
          payload: errorMessage,
        });
        toast.error(errorMessage);
        reject(error);
      }
    });
  };

// Get sellers
export const getSellers = (staff_id) => async (dispatch) => {
  try {
    const { data } = await api.fetchSellers(staff_id);
    dispatch({ type: GET_SELLERS, payload: data });
  } catch (error) {
    console.log(error);
    const errorMessage = 
      error.response?.data?.message || 
      error.message || 
      "Failed to fetch sellers.";
    
    dispatch({
      type: GET_SELLERS_FAIL,
      payload: errorMessage,
    });
    toast.error(errorMessage);
  }
};

// Get seller Details
export const getSellerDetail = (seller_id) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const { data } = await api.fetchSellerDetail(seller_id);
    dispatch({ type: GET_SELLER, payload: data });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.log(error);
    const errorMessage = 
      error.response?.data?.message || 
      error.message || 
      "Failed to fetch seller details.";
    
    dispatch({
      type: GET_SELLER_FAIL,
      payload: errorMessage,
    });
    toast.error(errorMessage);
  }
};

// Soft Delete Seller
export const softDeleteSeller = (seller_id, staff_id) => async (dispatch) => {
  try {
    const { data } = await api.softDeleteSellerApi(seller_id, staff_id);
    dispatch({ type: DELETE_SELLER, payload: data });
    toast.success(data.message);
    dispatch(getSellers(staff_id));
  } catch (error) {
    console.log(error);
    const errorMessage = 
      error.response?.data?.message || 
      error.message || 
      "Failed to delete seller.";
    toast.error(errorMessage);
  }
};
// Delete Seller
export const deleteSeller = (seller_id, staff_id) => async (dispatch) => {
  try {
    const { data } = await api.deleteSellerApi(seller_id, staff_id);
    dispatch({ type: DELETE_SELLER, payload: data });
    toast.success(data.message);
    dispatch(getSellers(staff_id));
  } catch (error) {
    console.log(error);
    const errorMessage = 
      error.response?.data?.message || 
      error.message || 
      "Failed to delete seller.";
    toast.error(errorMessage);
  }
};

// Verify Seller
//
// `activation` is an OPTIONAL bag of fields captured at verify time for
// seeded vendors (whose Users row was created with a placeholder
// `@lusuku.pending` email and no password). The backend handler at
// controllers/sellerController.js::verifySeller upgrades the email,
// backfills phoneNumber, and auto-sends a password-reset link so the
// vendor can log in — but only when `sellerVerification === "VERIFIED"`
// and only when the current email is still the seed placeholder.
// Non-seeded vendors: pass nothing and the verify behaves identically
// to the pre-activation code path.
//
// Shape: { email?, phoneNumber?, clientUrl? }
//   - clientUrl should be the full base that the /reset-password/:token/:id
//     route lives at in the dashboard, e.g. `${window.location.origin}/reset-password`.
export const verifySeller = (
  seller_id,
  staff_id,
  sellerVerification,
  sellerVerificationDate,
  activation
) => {
  return async (dispatch) => {
    dispatch({ type: UPDATE_SELLER_REQUEST });

    try {
      const { data } = await api.verifySellerApi(seller_id, staff_id, {
        sellerVerification,
        sellerVerificationDate,
        // Only include activation fields when provided — keeps the
        // payload identical to the old shape for non-seeded flows.
        ...(activation?.email ? { email: activation.email } : {}),
        ...(activation?.phoneNumber
          ? { phoneNumber: activation.phoneNumber }
          : {}),
        ...(activation?.clientUrl
          ? { clientUrl: activation.clientUrl }
          : {}),
      });
      dispatch({ type: UPDATE_SELLER_SUCCESS, payload: data });
      dispatch(getSellerDetail(seller_id));
      toast.success(data.message);
    } catch (error) {
      console.log(error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to verify seller.";
      toast.error(errorMessage);
    }
  };
};

// Reactivate Seller
export const reactivateSeller = (seller_id, staff_id) => {
  return async (dispatch) => {
    dispatch({ type: UPDATE_SELLER_REQUEST });

    try {
      const { data } = await api.reactivateSellerApi(seller_id, staff_id, {
        isActive: true,
      });
      dispatch({ type: UPDATE_SELLER_SUCCESS, payload: data });
      dispatch(getSellerDetail(seller_id));
      dispatch(getSellers(staff_id)); // Add this line to refetch updated sellers
      toast.success(data.message);
    } catch (error) {
      console.log(error);
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        "Failed to reactivate seller.";
      toast.error(errorMessage);
    }
  };
};

// Resend Verification Email
export const resendVerificationEmail = (seller_id) => {
  return async (dispatch) => {
    try {
      const { data } = await api.resendVerificationEmailApi(seller_id);
      toast.success(data.message || "Verification email sent successfully");
      return data;
    } catch (error) {
      console.error("Error resending verification email:", error);
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        "Failed to resend verification email.";
      toast.error(errorMessage);
      throw error;
    }
  };
};

// Get all vendors for super admin (Restaurant, Retail, Wholesale, Healthcare)
export const getAllVendorsForSuperAdmin = () => async (dispatch) => {
  try {
    dispatch({ type: FETCH_ALL_VENDORS_FOR_SUPERADMIN_REQUEST });
    dispatch({ type: START_LOADING });

    const { data } = await api.apiFetchAllVendorsForSuperAdmin();
    
    dispatch({
      type: FETCH_ALL_VENDORS_FOR_SUPERADMIN_SUCCESS,
      payload: data.vendors,
    });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.error("Error fetching all vendors for super admin:", error);
    const errorMessage = 
      error.response?.data?.message || 
      error.message || 
      "Failed to fetch vendors.";
    
    dispatch({
      type: FETCH_ALL_VENDORS_FOR_SUPERADMIN_FAILURE,
      payload: errorMessage,
    });
    dispatch({ type: END_LOADING });
    toast.error(errorMessage);
  }
};

// Update Seller Profile (Super Admin only)
export const updateSellerProfile = (seller_id, profileData) => {
  return async (dispatch) => {
    dispatch({ type: UPDATE_SELLER_REQUEST });

    try {
      const { data } = await api.updateSellerProfileApi(seller_id, profileData);
      dispatch({ type: UPDATE_SELLER_SUCCESS, payload: data });
      dispatch(getSellerDetail(seller_id));
      toast.success(data.message || "Profile updated successfully");
      return data;
    } catch (error) {
      console.error("Error updating seller profile:", error);
      const errorMessage = 
        error.response?.data?.message || 
        error.message || 
        "Failed to update seller profile.";
      
      dispatch({ type: UPDATE_SELLER_FAIL, payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };
};
